// Share-link state for the calculator. Serializes the estimate *setup* (the result
// itself is recomputed) into query params, so a copied link restores the exact same
// estimate for the recipient. Only deviations from the defaults are written, which
// keeps the pristine page URL clean (/grid-cost-calculator).
//
// Params:
//   scenario=standalone      default "addon" (omitted)
//   lenses=8                 default 3 (omitted)
//   market=europe            profile name, lowercased; omitted for the default profile
//   preset=indie|highend     named budget preset; omitted for commercial / custom sets
//   r=key:value,...          custom rate overrides (written only when no named preset:
//                            a preset reproduces its seeded rates by itself)
//   k=key:value,...          timing-knob overrides
//   p=key,...                pay-for checklist lines flipped from the scenario defaults
//
// Parsing is defensive: unknown keys, non-numbers and negatives are dropped, lists
// are capped — the URL is user input like any other.

export const SHARE_DEFAULTS = { scenario: 'addon', lensCount: 3, preset: 'commercial' };

const KEY_RE = /^[a-z0-9_]+$/; // rate/knob/pay keys are slugs
const MAX_PAIRS = 24; // abuse guard against absurdly long override lists
const PRESET_KEYS = ['indie', 'commercial', 'highend'];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const joinPairs = (obj) => Object.keys(obj).sort().map((k) => `${k}:${obj[k]}`).join(',');

const parsePairs = (raw) => {
  const out = {};
  if (!raw) return out;
  raw.split(',').slice(0, MAX_PAIRS).forEach((pair) => {
    const [key, value] = pair.split(':');
    if (!KEY_RE.test(key)) return;
    const n = num(value);
    if (n === null || n < 0) return;
    out[key] = n;
  });
  return out;
};

export function buildShareParams({
  scenario, lensCount, profile, activePreset, overrides, knobOverrides, paying, payingDefaults,
}) {
  const p = new URLSearchParams();
  if (scenario && scenario !== SHARE_DEFAULTS.scenario) p.set('scenario', scenario);
  if (lensCount !== SHARE_DEFAULTS.lensCount) p.set('lenses', String(lensCount));
  if (profile && !profile.is_default) p.set('market', profile.name.toLowerCase());
  if (activePreset && activePreset !== SHARE_DEFAULTS.preset) {
    p.set('preset', activePreset);
  } else if (activePreset === null) {
    // Custom set: serialize the effective overrides (preset seeds + manual edits).
    const r = joinPairs(overrides);
    if (r) p.set('r', r);
  }
  const k = joinPairs(knobOverrides);
  if (k) p.set('k', k);
  if (paying && payingDefaults) {
    const flipped = Object.keys(payingDefaults)
      .filter((key) => Boolean(paying[key]) !== Boolean(payingDefaults[key]))
      .sort();
    if (flipped.length) p.set('p', flipped.join(','));
  }
  return p;
}

export function parseShareParams(searchParams) {
  const out = {};

  const scenario = searchParams.get('scenario');
  if (scenario === 'addon' || scenario === 'standalone') out.scenario = scenario;

  const rawLenses = searchParams.get('lenses');
  if (rawLenses !== null) {
    const n = num(rawLenses);
    if (n !== null) out.lensCount = Math.min(20, Math.max(1, Math.round(n)));
  }

  const market = searchParams.get('market');
  if (market && /^[a-z0-9 -]+$/i.test(market)) out.market = market.toLowerCase();

  const preset = searchParams.get('preset');
  if (PRESET_KEYS.includes(preset)) out.preset = preset;

  const rates = parsePairs(searchParams.get('r'));
  if (Object.keys(rates).length) out.rates = rates;

  const knobs = parsePairs(searchParams.get('k'));
  if (Object.keys(knobs).length) out.knobs = knobs;

  const payRaw = searchParams.get('p');
  if (payRaw) {
    const keys = payRaw.split(',').slice(0, MAX_PAIRS).filter((k) => KEY_RE.test(k));
    if (keys.length) out.payFlip = keys;
  }

  return out;
}
